import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import {
  LinearProgress,
  InputAdornment,
  IconButton,
  Collapse,
  Fade,
  Chip,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  NotificationsOutlined as NotificationsNoneIcon,
  Notifications as NotificationsIcon,
  SubscriptionsOutlined as SubscriptionsNoneIcon,
  Subscriptions as SubscriptionsIcon,
} from '@mui/icons-material';
import {
  IState,
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import {
  ProgressContainer,
  ControlContainer,
  Label,
  Input,
  ValuesContainer,
  Value,
  WarnContainer,
  ChipContainer,
  InfoContainer,
  StyledAvatar,
  TeamContainer,
  TeamActionContainer,
  TeamGamePartContainer,
  DefaultGamePartContainer,
} from './styles';
import { IAppState } from '../../../../state/reducers';
import { selectIsUserExchangeVendor } from '../../../../state/user/userOrgRole';
import {
  ValidationKey,
  queryNotificationSettings,
  toggleNotificationSettings,
  updateSettings,
  saveSettings,
  updateEmail,
  updatePhoneNumber,
  addEmail,
  addPhoneNumber,
  removeEmail,
  removePhoneNumber,
  updateTeamFilter,
} from '../../../../state/exchange/notificationSettings';
import { selectValidation } from '../../../../state/validation';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogActionsOverride,
  ActionContainer,
} from '../../../Shared/styles/dialog';
import { VaultButton, Warning } from '../../../Shared';
import ValidationError from '../../../Shared/ValidationError';
import { selectExchangeQualifiersOptions, ILookupOption } from '../../../../state/lookups';
import CheckBoxSelect from '../../../Shared/CheckBoxSelect';

class ExchangeNotificationSettings extends React.Component<IProps, IState> {
  componentDidMount() {
    const { onSettingsQuery } = this.props;
    onSettingsQuery();
  }

  onAddEmail(e?: React.KeyboardEvent) {
    if (e && e.key !== 'Enter') {
      return;
    }
    const {
      emails,
      editedEmail,
      addEmailConnect,
    } = this.props;
    if (editedEmail && !emails.includes(editedEmail.toLowerCase())) {
      if (e && e.key === 'Enter') {
        const ref = e.nativeEvent.srcElement;
        // @ts-ignore
        ref.blur();
      }
      addEmailConnect();
    }
  }

  onToggleTeamSubscription(teamId: string, subscribe: boolean) {
    const { subscription, onUpdateSettings } = this.props;

    const subscribedTeams = subscription.subscribedTeams.reduce((teams, team) => {
      if (team.teamId === teamId) {
        teams.push({
          ...team,
          subscribe,
        });
      } else {
        teams.push(team);
      }
      return teams;
    }, []);

    onUpdateSettings({
      ...subscription,
      subscribedTeams,
    });
  }

  onToggleTeamNotification(teamId: string, notify: boolean) {
    const { subscription, onUpdateSettings } = this.props;

    const subscribedTeams = subscription.subscribedTeams.reduce((teams, team) => {
      if (team.teamId === teamId) {
        teams.push({
          ...team,
          notify,
        });
      } else {
        teams.push(team);
      }
      return teams;
    }, []);

    onUpdateSettings({
      ...subscription,
      subscribedTeams,
    });
  }

  onUpdateDefaultExchangeQualifiers(qualifiers: string[]) {
    const { subscription, qualifierLookup, onUpdateSettings } = this.props;

    if (qualifiers.includes(undefined)) {
      // note: assume triggered via parent category
      return;
    }

    onUpdateSettings({
      ...subscription,
      exchangeQualifier: qualifiers,
    });
  }

  onUpdateTeamExchangeQualifiers(qualifiers: string[], teamId: string) {
    const { subscription, qualifierLookup, onUpdateSettings } = this.props;

    if (qualifiers.includes(undefined)) {
      // note: assume triggered via parent category
      return;
    }

    const teamIndex = subscription.subscribedTeams.findIndex((team) => team.teamId === teamId);
    subscription.subscribedTeams[teamIndex].exchangeQualifier = qualifiers;

    onUpdateSettings({
      ...subscription,
      subscribedTeams: subscription.subscribedTeams,
    });
  }

  onUpdateExchangeQualifiersByGroup(option: ILookupOption, teamId: string = null) {
    const children: Array<string> = option.children.map(({ value }) => value);
    const getOptions = (qualifiers: Array<string>): Array<string> => {
      if (children.every((value) => qualifiers.includes(value))) {
        return qualifiers.filter((value) => !children.includes(value));
      }
      return [...qualifiers, ...children.filter((value) => !qualifiers.includes(value))];
    };
    if (teamId) {
      const { subscription } = this.props;
      const teamIndex = subscription.subscribedTeams.findIndex((team) => team.teamId === teamId);
      const qualifiers = subscription.subscribedTeams[teamIndex].exchangeQualifier;
      this.onUpdateTeamExchangeQualifiers(getOptions(qualifiers), teamId);
    } else {
      const { currentExchangeQualifier: qualifiers } = this.props;
      this.onUpdateDefaultExchangeQualifiers(getOptions(qualifiers));
    }
  }

  render() {
    const {
      isLoading,
      isLoaded,
      isUserExchangeVendor,
      emails,
      editedEmail,
      validation,
      subscribedTeams,
      teamFilter,
      teams,
      qualifierLookup,
      currentExchangeQualifier,
      onEmailChange,
      onEmailRemove,
      onUpdateTeamFilter,
      onSave,
      onClose,
    } = this.props;

    let subscribed: Array<{
      teamId: string,
      teamCode: string,
      teamName: string,
      teamImage: string,
      subscribe: boolean,
      notify: boolean,
      exchangeQualifier: string[],
    }> = isLoaded ? subscribedTeams.map(({
      teamId, teamCode, subscribe, notify, exchangeQualifier,
    }) => {
      const { TeamName, TeamImage, TeamCode } = teams.find(({ _id }) => teamId === _id);
      return {
        teamId,
        teamCode: TeamCode,
        teamName: TeamName,
        teamImage: TeamImage,
        subscribe,
        notify,
        exchangeQualifier,
      };
    }) : [];

    // note: apply team filter to subscribed teams
    if (teamFilter) {
      subscribed = subscribed.filter(({ teamName }) => teamName.toLowerCase().includes(teamFilter.toLowerCase()));
    }

    // note: sort teams alphabetically
    subscribed = subscribed.sort((a, b) => a.teamName.localeCompare(b.teamName));

    return (
      <>
        <DialogHeader
          data-auto="SubscriptionDialog"
        >
          <DialogHeaderTitle>
            Subscription Settings
          </DialogHeaderTitle>
        </DialogHeader>
        <ProgressContainer>
          {isLoading && <LinearProgress />}
        </ProgressContainer>
        {isLoaded && (
        <>
          <Collapse in={validation.email.invalid}>
            <ValidationError errors={validation.email.errors} />
          </Collapse>
          <ControlContainer>
            <Label>
              E-Mail
            </Label>
            <Input
              type="email"
              placeholder="Enter e-mail addresses to receive notifications"
              data-auto="NotificationEmailInput"
              notched={false}
              value={editedEmail}
              onChange={(e) => onEmailChange(e.target.value)}
              onKeyDown={(e) => this.onAddEmail(e)}
              endAdornment={(
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => this.onAddEmail()}
                    edge="end"
                    disabled={!editedEmail}
                    size="large"
                  >
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              )}
            />
          </ControlContainer>
          <WarnContainer>
            <Warning
              isWarning={emails.includes(editedEmail.toLowerCase())}
              message={`Email "${editedEmail}" already exists`}
            />
          </WarnContainer>
          <ChipContainer>
            {emails.map((email, index) => (
              <Chip
                key={index}
                label={email}
                onDelete={() => onEmailRemove(index)}
                variant="outlined"
              />
            ))}
          </ChipContainer>
          {subscribedTeams.length > 0 && (
          <>
            <ControlContainer>
              <Label>
                Default Game Parts
              </Label>
              <DefaultGamePartContainer>
                <CheckBoxSelect
                  onChange={(result) => { this.onUpdateDefaultExchangeQualifiers(result); }}
                  onChangeGroup={(option: ILookupOption) => { this.onUpdateExchangeQualifiersByGroup(option); }}
                  isLoading={isLoading}
                  options={qualifierLookup}
                  selectedOptions={currentExchangeQualifier}
                  noneSelectedText="Using All Game Parts"
                  dataAuto="DefaultGameParts"
                />
              </DefaultGamePartContainer>
            </ControlContainer>
            <ControlContainer>
              <Label>
                Teams
              </Label>
              <Input
                type="text"
                placeholder="Filter teams"
                data-auto="FilterTeamsInput"
                notched={false}
                value={teamFilter}
                onChange={(e) => onUpdateTeamFilter(e.target.value)}
              />
            </ControlContainer>
            <ValuesContainer>
              <Warning
                isWarning={!subscribed.length}
                message="No matching subscriptions found"
              />
              {subscribed.map(({
                teamId,
                teamName,
                teamCode,
                teamImage,
                subscribe,
                notify,
                exchangeQualifier,
              }, index) => (
                <Fade in key={index}>
                  <Value>
                    <TeamContainer>
                      <StyledAvatar
                        alt={teamName}
                        src={teamImage}
                        data-auto="SubscribeTeamListItem"
                        data-teamcode={teamCode}
                      />
                      {teamName} {teamCode ? `(${teamCode})` : null }
                    </TeamContainer>
                    <TeamActionContainer>
                      {!isUserExchangeVendor && (
                      <IconButton
                        onClick={() => this.onToggleTeamSubscription(teamId, !subscribe)}
                        className={`${subscribe ? 'active' : null}`}
                        data-auto="TeamSubscribeBtn"
                        size="large"
                      >
                        { subscribe
                          ? <SubscriptionsIcon />
                          : <SubscriptionsNoneIcon />}
                      </IconButton>
                      )}
                      <IconButton
                        onClick={() => this.onToggleTeamNotification(teamId, !notify)}
                        className={`${notify ? 'active' : null}`}
                        data-auto="TeamNotificationBtn"
                        size="large"
                      >
                        { notify
                          ? <NotificationsIcon />
                          : <NotificationsNoneIcon />}
                      </IconButton>
                      <TeamGamePartContainer>
                        <CheckBoxSelect
                          onChange={(result) => { this.onUpdateTeamExchangeQualifiers(result, teamId); }}
                          onChangeGroup={(option: ILookupOption) => { this.onUpdateExchangeQualifiersByGroup(option, teamId); }}
                          isLoading={isLoading}
                          options={qualifierLookup}
                          selectedOptions={exchangeQualifier}
                          noneSelectedText="Using Default Game Parts"
                          dataAuto="TeamGameParts"
                        />
                      </TeamGamePartContainer>
                    </TeamActionContainer>
                  </Value>
                </Fade>
              ))}
            </ValuesContainer>

          </>
          )}
          <InfoContainer>
            <h1>Notifications when content is available and download failures occur.</h1>
          </InfoContainer>
        </>
        )}
        <DialogActionsOverride disableSpacing>
          <ActionContainer>
            <VaultButton
              variant="plainOnPaper"
              onClick={onClose}
              data-auto="SubscriptionCancelBtn"
            >
              Cancel
            </VaultButton>
            <VaultButton
              variant="primaryOnWhite"
              onClick={() => { onSave(); onClose(); }}
              data-auto="SubscriptionSaveBtn"
            >
              Save
            </VaultButton>
          </ActionContainer>
        </DialogActionsOverride>
      </>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => {
  const validation = {
    email: selectValidation(ValidationKey.Email, state.validation),
  };

  return {
    isLoading: state.exchange?.notificationSettings?.isLoading ?? false,
    isLoaded: state.exchange?.notificationSettings?.isLoaded ?? false,
    isUserExchangeVendor: selectIsUserExchangeVendor(state),
    subscription: state.exchange?.notificationSettings?.subscription,
    emails: state.exchange?.notificationSettings?.subscription?.emailAddresses ?? [],
    phoneNumbers: state.exchange?.notificationSettings?.subscription?.phoneNumbers ?? [],
    editedEmail: state.exchange?.notificationSettings?.editedEmail ?? '',
    editedPhoneNumber: state.exchange?.notificationSettings?.editedPhoneNumber ?? '',
    subscribedTeams: (state.exchange?.notificationSettings?.subscription?.subscribedTeams ?? [])
      .filter(({ subscribe, notify }) => subscribe || notify),
    teams: state.exchange?.notificationSettings?.teams ?? [],
    teamFilter: state.exchange?.notificationSettings?.teamFilter ?? '',
    validation,
    qualifierLookup: selectExchangeQualifiersOptions(state),
    currentExchangeQualifier: state.exchange?.notificationSettings?.subscription?.exchangeQualifier ?? [],
  };
};

const mapDispatchToProps: IDispatchProps = {
  onSettingsQuery: queryNotificationSettings,
  onEmailChange: updateEmail,
  onPhoneNumberChange: updatePhoneNumber,
  onPhoneNumberAdd: addPhoneNumber,
  onEmailRemove: removeEmail,
  onPhoneNumberRemove: removePhoneNumber,
  onUpdateTeamFilter: updateTeamFilter,
  onUpdateSettings: updateSettings,
  onSave: saveSettings,
  onClose: toggleNotificationSettings,
  addEmailConnect: addEmail,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ExchangeNotificationSettings);
